import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { checkNavPermission, ROUTES } from '@futrli/shared-services';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { consolidationsQuery, FetchConsolidationsResult } from '../../../queries/consolidations';
import { currentUserQuery, CurrentUserResult } from '../../../queries/currentUser';
import {
  FetchWorkspacesListingResult,
  workspacesListingQuery
} from '../../../operations/workspaces/queries/workspacesListingQuery';
import { getUserName } from '../../../utils/users/getUserName';

export interface PrimaryNavFlags {
  hasMembersPermission: boolean;
  showMyProfile: boolean;
}

export interface PrimaryNavViewState extends ApiViewState {
  currentUser?: CurrentUserResult;
  userName: string;
  flags: PrimaryNavFlags;
  workspaces: FetchWorkspacesListingResult;
  workspace?: FetchWorkspacesListingResult[number];
  consolidations: FetchConsolidationsResult;
}

export class PrimaryNavView extends ApiView {
  private currentUserObs = this.addQuery(currentUserQuery());
  private workspacesObs = this.addQuery(workspacesListingQuery());
  private consolidationsObs;

  public state: PrimaryNavViewState = {
    ...getDefaultApiViewState(),
    userName: '',
    flags: {
      hasMembersPermission: false,
      showMyProfile: false
    },
    workspaces: [],
    consolidations: []
  };

  constructor(private featureFlags: LDFlagSet, private workspaceId: string) {
    super();
    this.consolidationsObs = this.addQuery(consolidationsQuery(workspaceId, { enabled: !!workspaceId }));
    this.subscribeToObservers();
  }

  private getShowMyProfile(currentUser?: CurrentUserResult): boolean {
    const isSageIdUser = !!currentUser?.sageid_id;
    const isSxpManaged = !!currentUser?.is_sxp_managed;
    return (isSageIdUser && !!this.featureFlags.switchLocale) || (!isSageIdUser && !isSxpManaged);
  }

  protected getNewState(): PrimaryNavViewState {
    const currentUser = this.getResult(this.currentUserObs).data;
    const workspaces = this.getResult(this.workspacesObs).data || [];
    const workspace = workspaces.find(workspace => workspace.id === this.workspaceId);
    const organisations = workspace?.organisations || [];
    const onlyExternalOrgs = organisations.length > 0 && organisations.every(org => org.is_externally_managed);

    return {
      ...super.getNewState(),
      currentUser,
      userName: getUserName(currentUser?.name),
      workspaces,
      workspace,
      flags: {
        hasMembersPermission:
          checkNavPermission(workspace?.permissions ?? null, ROUTES.MEMBERS_ROUTE) && !onlyExternalOrgs,
        showMyProfile: this.getShowMyProfile(currentUser)
      },
      consolidations: this.getResult(this.consolidationsObs).data || []
    };
  }
}
